<template>
	<div class="grid-item pilot-identity" style="color: white !important">
		<div class="header">
			<div class="col grow-max">
				<div class="heading h1">{{ pilot.callsign }}</div>
				<div class="heading h2">({{ pilot.name || "UNIDENTIFIED" }})</div>
			</div>
			<div class="col header-actions">
				<button
					v-if="embedded"
					class="foundry-open"
					v-tip="{ title: 'Open in Foundry', body: 'Open this pilot\'s sheet in Foundry.' }"
					@click.stop="openInFoundry('pilot', pilot.callsign)"
				>
					<i class="mdi mdi-open-in-new" aria-hidden="true"></i>
				</button>
				<img src="/faction-logos/barony.svg" />
			</div>
		</div>
		<div class="body">
			<div class="add-padding ident-record">
				Union Administrative RM-4 Pilot Identification Protocol (IDENT) Record {{ pilot.id }}
			</div>
			<div class="flex-container-rows">
				<div class="row flex-container-cols add-padding">
					<div class="col grow-max flex-container-rows" style="padding-top: 5px">
						<div class="row flex-container-cols">
							<div class="col col-primary">
								<span class="flavor-text">
									Callsign: <b class="accent--text">{{ capitalize(pilot.callsign) }}</b
									><br />
									Name (or legal alias):
									<b v-if="pilot.name" class="accent--text">{{ pilot.name }}</b
									><b v-else class="unregistered">[UNREGISTERED]</b><br />
									Background:
									<b v-if="pilot.background" class="accent--text">{{ pilot.background }}</b
									><b v-else class="unregistered">[UNREGISTERED]</b></span
								>
							</div>
							<div class="col">
								<div
									v-for="stamp in identStamps"
									:key="stamp.text"
									:class="stamp.ok ? 'verified' : 'unverified'"
								>
									{{ stamp.text }}
								</div>
							</div>
						</div>
						<div class="ident-section">
							FRAME CONFIGURATION OPTIONS
							<span class="subtle--text">("H.A.S.E" OMNINET VAULT REMIT)</span>
						</div>
						<div class="row" style="padding-top: 5px">
							<span style="font-size: 22px; line-height: 15px">
								[ HULL:
								<span class="stat-text accent--text" style="font-size: 24px">
									{{ pilot.hase[0] }}
								</span>
								AGI:
								<span class="stat-text accent--text" style="font-size: 24px">
									{{ pilot.hase[1] }}
								</span>
								SYS:
								<span class="stat-text accent--text" style="font-size: 24px">
									{{ pilot.hase[2] }}
								</span>
								ENG:
								<span class="stat-text accent--text" style="font-size: 24px">
									{{ pilot.hase[3] }}
								</span>
								]
							</span>
						</div>
						<div class="row flex-container-cols ident-section">
							<div class="col col-share">
								<span>PILOT SKILL TRIGGER AUDIT</span>
								<br />
								<div class="chip-container" v-for="skill in pilot.skills" :key="skill.name">
									<span class="chip" v-tip="skillTip(skill)"
										><i aria-hidden="true" class="notranslate cci cci-skill"></i
										>{{ skill.name }}</span
									>
								</div>
							</div>
							<div class="col col-share">
								<span>PILOT TALENT AUDIT</span>
								<br />
								<div class="chip-container" v-for="talent in pilot.talents" :key="talent">
									<span class="chip" v-tip="lookup.talent(talent)"
										><i aria-hidden="true" class="notranslate cci cci-talent"></i>{{ talent }}</span
									>
								</div>
							</div>
						</div>
						<div v-if="pilot.level > 0 || pilot.licenses.length" class="row flex-container-cols">
							<div class="col" style="padding-top: 5px">
								<span>PROCUREMENT LICENSE AUDIT: LEVEL {{ pilot.level }}</span>
								<br />
								<div class="chip-container" v-for="license in pilot.licenses" :key="license">
									<span class="chip" v-tip="lookup.license(license)"
										><i aria-hidden="true" class="notranslate cci cci-license"></i
										>{{ license }}</span
									>
								</div>
							</div>
						</div>
					</div>
					<div class="col">
						<div class="pilot-image-container">
							<div class="pilot-image-border">
								<img
									:src="pilot.portrait"
									class="portrait"
									@error="e => (e.target.src = './nodata.png')"
								/>
							</div>
						</div>
					</div>
				</div>
				<!-- Bond and pilot gear split the row 50/50; gear sits under the portrait -->
				<div v-if="pilot.bond || hasGear" class="row flex-container-cols add-padding ident-section">
					<div class="col col-share">
						<template v-if="pilot.bond">
							<span
								>BOND AUDIT // {{ pilot.bond.name.toUpperCase() }}
								<span
									v-if="pilot.bond.xp !== null || pilot.bond.stress !== null"
									class="subtle--text"
								>
									[<template v-if="pilot.bond.xp !== null">
										XP: <span class="accent--text">{{ pilot.bond.xp }}</span></template
									>
									<template v-if="pilot.bond.stress !== null">
										STRESS: <span class="accent--text">{{ pilot.bond.stress }}</span></template
									>
									]
								</span>
							</span>
							<br />
							<div class="chip-container" v-for="power in pilot.bond.powers" :key="power.name">
								<span
									class="chip"
									v-tip="power.description ? { title: power.name, body: power.description } : null"
									><i aria-hidden="true" class="notranslate cci cci-corebonus"></i
									>{{ power.name }}</span
								>
							</div>
							<div class="chip-container" v-for="burden in pilot.bond.burdens" :key="burden.name">
								<span class="chip burden-chip"
									><i aria-hidden="true" class="notranslate cci cci-status-danger-zone"></i
									>{{ burden.name
									}}<template v-if="burden.segments">
										[{{ burden.progress }}/{{ burden.segments }}]</template
									></span
								>
							</div>
						</template>
					</div>
					<div class="col col-share">
						<template v-if="hasGear">
							<span>PILOT GEAR AUDIT</span>
							<br />
							<!-- Armor | Weapons on the first line, items on the second -->
							<div class="gear-line">
								<div class="chip-container" v-for="(item, i) in pilot.gear.armor" :key="'a' + i">
									<span class="chip" v-tip="lookup.gear(item)"
										><i aria-hidden="true" class="notranslate cci cci-pilot"></i>{{ item }}</span
									>
								</div>
								<span
									v-if="pilot.gear.armor.length && pilot.gear.weapons.length"
									class="gear-divider"
									>|</span
								>
								<div class="chip-container" v-for="(item, i) in pilot.gear.weapons" :key="'w' + i">
									<span class="chip" v-tip="lookup.gear(item)"
										><i aria-hidden="true" class="notranslate cci cci-weapon"></i>{{ item }}</span
									>
								</div>
							</div>
							<div v-if="pilot.gear.items.length" class="gear-line">
								<div class="chip-container" v-for="(item, i) in pilot.gear.items" :key="'g' + i">
									<span class="chip" v-tip="lookup.gear(item)"
										><i aria-hidden="true" class="notranslate cci cci-generic-item"></i
										>{{ item }}</span
									>
								</div>
							</div>
						</template>
					</div>
				</div>
			</div>
			<div class="flex-container-cols modal-buttons">
				<div class="row biometrics-container">
					<div class="biometrics flex-container-cols" @click="pilotModal">
						<div>
							<i
								aria-hidden="true"
								class="v-icon notranslate mdi mdi-fingerprint theme--dark grey--text text--darken-2"
								style="font-size: 36px; margin-top: 36px"
							></i>
						</div>
						<div style="width: 100%">
							BIOMETRIC RECORD VALID [[{{ randomNumber(14, 22) }}PB]]<br />
							OHM C//{{ timeStamp(pilot.updated) }}
						</div>
					</div>
				</div>
				<div v-if="pilot.mech" class="row biometrics-container">
					<div class="mech-record flex-container-cols" @click="openChassis">
						<div style="width: 100%">
							MECHANICAL BLUEPRINT VALID [[{{ randomNumber(14, 22) }}TB]] <br />
							{{ pilot.mech.manufacturer.toUpperCase() }}-{{
								pilot.mech.frameName.toUpperCase()
							}}
							:: "{{ pilot.mech.name.toUpperCase() }}"
						</div>
						<div>
							<i
								aria-hidden="true"
								class="v-icon notranslate cci cci-reserve-mech theme--dark grey--text text--darken-2 larger"
								style="font-size: 42px; margin-top: 1em"
							></i>
						</div>
					</div>
				</div>
				<div v-else class="row biometrics-container">
					<div class="mech-record no-blueprint flex-container-cols">
						<div style="width: 100%">
							ERR: NO MECHANICAL BLUEPRINT ON FILE <br />
							CHASSIS UNAVAILABLE
						</div>
					</div>
				</div>
			</div>
			<hr role="separator" aria-orientation="horizontal" class="ma-2 v-divider theme--dark" />
			<div class="row row--dense">
				<span class="overline" style="line-height: 13px !important; opacity: 0.4">
					Improper use of this IDENT record and/or its constituent data by the record holder or any
					other persons is punishable under the DoJ/HR A-645-c. This record is the property of the
					Union Administrative Office and the information herein must be transmitted on request
					under NDL-C-DISCORDANT-BREATH encryption protocols. This RM-4 record must be updated every
					five (5) Cradle Standard Years of objective time to retain GMS licensing rights. Far-field
					operatives that anticipate deployments lasting longer than five Cradle Standard Years that
					have not been issued a man-portable Omninet Hook should apply for the RM-11-B IDENT
					Supplemental (b) Extension. Contact your local Union Adminstrative Officer for any other
					matters regarding this record.  V-CDL//M-265-114-831 (A)
				</span>
			</div>
		</div>
	</div>
</template>

<style scoped>
.larger::before {
	margin-top: 9px;
}

.mdi::before {
	margin-top: 9px;
}

.mech-record {
	margin-left: auto;
	text-align: right;
}

.modal-buttons {
	margin-top: 5px;
}

.gear-line {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
}

.gear-divider {
	margin: 0 0.4em;
	color: var(--text-pilot-code);
}

.no-blueprint {
	cursor: default;
	color: #e0566b;
}

.no-blueprint:hover {
	background-color: transparent !important;
}

.burden-chip {
	border-color: var(--primary-color);
}
</style>

<script>
import "external-svg-loader";
import PilotModal from "@/components/modals/PilotModal.vue";
import { lookup } from "@/utils/lancerInfo";
import { embedded, openInFoundry } from "@/data/liveState.js";

// Renders one pilot's IDENT card from the normalized shape built in
// src/data/pilots.js (skills/talents/licenses arrive as display strings).
export default {
	props: {
		animate: {
			type: Boolean,
			required: true,
		},
		pilot: {
			type: Object,
			required: true,
		},
	},
	data() {
		return { lookup };
	},
	computed: {
		// Inside Foundry the card offers to open the sheet there
		embedded() {
			return embedded;
		},
		// Status stamps reflect what's actually on file
		identStamps() {
			return [
				{ ok: true, text: "CALLSIGN AVAILABLE" },
				this.pilot.name
					? { ok: true, text: "IDENTITY VERIFIED" }
					: { ok: false, text: "IDENTITY UNVERIFIED" },
				this.pilot.background
					? { ok: true, text: "PH/HR DATA REGISTERED" }
					: { ok: false, text: "PH/HR DATA UNREGISTERED" },
			];
		},
		hasGear() {
			const { armor, weapons, items } = this.pilot.gear;
			return armor.length + weapons.length + items.length > 0;
		},
	},
	watch: {
		// Leaving the page (or switching pilot via the URL) closes the biometric pop-up
		$route() {
			this.closeModal();
		},
	},
	beforeUnmount() {
		this.closeModal();
	},
	methods: {
		openInFoundry,
		closeModal() {
			this.modal?.close?.();
			this.modal = null;
		},
		// Core triggers come from the data; custom ones (e.g. Psionics) carry their own text
		skillTip(skill) {
			return (
				lookup.skill(skill.name) ||
				(skill.description ? { title: skill.name, body: skill.description } : null)
			);
		},
		capitalize(str) {
			return str
				.split(" ")
				.map(word => word[0].toUpperCase() + word.slice(1))
				.join(" ");
		},
		randomNumber(max, min) {
			const rand = Math.random() * (max - min) + min;
			const power = Math.pow(10, 2);
			return Math.floor(rand * power) / power;
		},
		timeStamp(input) {
			// Accepts a YAML date/datetime, an ISO string or an epoch-ms value (CompCon),
			// shown 2,990 years on in the setting's calendar; blank when missing.
			// Formatted in UTC so the stored value isn't shifted by the viewer's timezone.
			if (input === undefined || input === null || input === "") return "";
			const date = new Date(input);
			if (Number.isNaN(date.getTime())) return "";
			const iso = date.toISOString();
			const stamp = `${date.getUTCFullYear() + 2990}${iso.slice(4)}`;
			// A bare date (e.g. `updated: 2026-10-06`) has no time to show
			const dateOnly =
				date.getUTCHours() === 0 &&
				date.getUTCMinutes() === 0 &&
				date.getUTCSeconds() === 0 &&
				date.getUTCMilliseconds() === 0;
			return dateOnly ? stamp.slice(0, 10) : stamp;
		},
		openChassis() {
			this.$router.push({ path: "/chassis", query: { pilot: this.pilot.callsign } });
		},
		pilotModal() {
			this.modal = this.$oruga.modal.open({
				component: PilotModal,
				custom: true,
				trapFocus: true,
				props: {
					pilot: this.pilot,
				},
				class: "custom-modal",
				width: 1920,
			});
		},
	},
};
</script>
